import { LayoutModule } from '@angular/cdk/layout';
import {
    APP_BASE_HREF,
    CommonModule,
    DatePipe,
    PlatformLocation
} from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import {
    FeedbackEmailButtonModule,
    FeedbackModule,
    StartOverButtonModule,
    StartOverModule,
    TrademeCoreUiModule
} from '@classifieds-sell/trademe-core-ui';
import {
    TgBoxes2Module,
    TgButtons2Module,
    TgCardModule,
    TgGridModule,
    TgIconBasicArrowRightModule,
    TgIconBasicInfoFramedModule,
    TgIconBasicTickFramedModule,
    TgIconBasicWarningFramedModule,
    TgIconStopModule,
    TgInputModule,
    TgLoadingModule,
    TgModalModule,
    TgRackModule,
    TgSvgModule,
    TgTeleportModule
} from '@trademe/tangram';
import { TmDateComponent } from 'libs/trademe-core-ui/src/lib/tm-date/tm-date.component';
import { TmHeaderComponent } from 'libs/trademe-core-ui/src/lib/tm-header/tm-header.component';
import { PropertyHeaderComponent } from './components/property-header/property-header.component';
import { PropertyHomeComponent } from './components/property-home/property-home.component';
import { PropertyLandingComponent } from './components/property-landing/property-landing.component';
import { PropertyNotFoundComponent } from './components/property-not-found/property-not-found.component';
import { PropertyPageFooterComponent } from './components/property-page-footer/property-page-footer.component';
import { PropertyTabsComponent } from './components/property-tabs/property-tabs.component';
import { CreditAccountModule } from './credit-account/credit-account.module';
import { PropertyUiChildRouteGuard } from './guards/property-ui-child-route.guard';
import { PropertyUiRouteGuard } from './guards/property-ui-route.guard';
import { FormHelperService } from './helpers/form-helper.service';
import { PropertyUiInterceptor } from './interceptors/property-ui.interceptor';
import { ResidentialForSaleAgentPropertySummaryPageModule } from './pages/category-page-overrides/residential-for-sale-agent/property-summary-page.module';
import { ResidentialForSalePropertySummaryPageModule } from './pages/category-page-overrides/residential-for-sale/property-summary-page.module';
import { PropertyAgentPromotePageModule } from './pages/property-default-pages/property-agent-promote-page/property-agent-promote-page.module';
import { PropertyDescriptionPageComponent } from './pages/property-default-pages/property-description-page/property-description-page.component';
import { PropertyInformationPageComponent } from './pages/property-default-pages/property-information-page/property-information-page.component';
import { PropertyPhotosPageModule } from './pages/property-default-pages/property-photos-page/property-photos-page.module';
import { PropertyPromotePageModule } from './pages/property-default-pages/property-promote-page/property-promote-page.module';
import { PropertySummaryPageModule } from './pages/property-default-pages/property-summary-page/property-summary-page.module';
import { ListStepsRoutingModule } from './property-ui-routes';
import { PropertyAboutSectionModule } from './sections/property-default-sections/property-about-section/property-about-section.module';
import { PropertyAgentPromoteSectionModule } from './sections/property-default-sections/property-agent-promote-section/property-agent-promote-section.module';
import { PropertyInformationSectionModule } from './sections/property-default-sections/property-information-section/property-information-section.module';
import { PropertyPromoteSectionModule } from './sections/property-default-sections/property-promote-section/property-promote-section.module';
import { PropertyOpenHomeTimesSectionModule } from './sections/residential-for-sale/property-open-home-times-section/property-open-home-times-section.module';
import { AnalyticsService } from './services/analytics-service';
import { InitService } from './services/init-service';
import { ListingDurationService } from './services/listing-duration.service';
import {
    DraftEffects,
    DraftFacade,
    RouterEffects,
    RouterFacade,
} from '@classifieds-sell/state';
import * as fromDraft from '@classifieds-sell/state';
import { FeeCalculationFacade } from './state/fee-calculation/fee-calculation.facade';
import * as fromPing from '../../../state/src/lib/ping/ping.reducer';
import * as fromRouter from '@classifieds-sell/state';
import * as fromUser from '@classifieds-sell/state';
import { UserEffects, UserFacade } from '@classifieds-sell/state';
import {
    RouterService,
    RouterToStoreLinkService,
    TimeFromNumberPipe,
    TMStepIndicatorModule
} from '@classifieds-sell/shared';
import { PropertyDescriptionPageModule } from './pages/property-default-pages/property-description-page/property-description-page.module';
import { TmPageNotFoundIllustrationModule } from 'libs/trademe-core-ui/src/lib/tm-page-not-found-illustration/tm-page-not-found-illustration.module';

const COMPONENTS = [
    PropertyHeaderComponent,
    PropertyLandingComponent,
    PropertyHomeComponent,
    PropertyNotFoundComponent,
    PropertyTabsComponent,
    PropertyPageFooterComponent,
    PropertyInformationPageComponent
];

@NgModule({
    imports: [
        CommonModule,
        CreditAccountModule,
        EffectsModule.forFeature([DraftEffects]),
        EffectsModule.forFeature([RouterEffects]),
        EffectsModule.forFeature([UserEffects]),
        FeedbackModule,
        FeedbackEmailButtonModule,
        StartOverModule,
        StartOverButtonModule,
        FormsModule,
        LayoutModule,
        ListStepsRoutingModule,
        PropertyAboutSectionModule,
        PropertyInformationSectionModule,
        PropertyDescriptionPageModule,
        PropertyPromoteSectionModule,
        PropertyAgentPromoteSectionModule,
        PropertySummaryPageModule,
        PropertyPhotosPageModule,
        PropertyPromotePageModule,
        PropertyAgentPromotePageModule,
        PropertyOpenHomeTimesSectionModule,
        ResidentialForSalePropertySummaryPageModule,
        ResidentialForSaleAgentPropertySummaryPageModule,
        StoreModule.forFeature(
            fromRouter.ROUTER_FEATURE_KEY,
            fromRouter.routerReducer
        ),
        StoreModule.forFeature(
            fromDraft.DRAFT_FEATURE_KEY,
            fromDraft.draftReducerFunc
        ),
        StoreModule.forFeature(
            fromUser.USER_FEATURE_KEY,
            fromUser.userReducerFunc
        ),
        StoreModule.forFeature(fromPing.PING_FEATURE, fromPing.pingReducerFunc),
        TgBoxes2Module,
        TgButtons2Module,
        TgCardModule,
        TgGridModule,
        TgIconBasicArrowRightModule,
        TgIconStopModule,
        TgIconBasicTickFramedModule,
        TgIconBasicInfoFramedModule,
        TgIconBasicWarningFramedModule,
        TgInputModule,
        TgLoadingModule,
        TgModalModule,
        TgRackModule,
        TgTeleportModule,
        TMStepIndicatorModule,
        TrademeCoreUiModule,
        TgSvgModule,
        TmPageNotFoundIllustrationModule
    ],
    declarations: [COMPONENTS],
    exports: [PropertyHeaderComponent, PropertyHomeComponent],
    providers: [
        PropertyUiChildRouteGuard,
        PropertyUiRouteGuard,
        RouterFacade,
        TmHeaderComponent,
        TmDateComponent,
        RouterService,
        RouterToStoreLinkService,
        AnalyticsService,
        InitService,
        {
            provide: APP_INITIALIZER,
            useFactory: (service: InitService) =>
                function () {
                    return service.init();
                },
            deps: [InitService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PropertyUiInterceptor,
            multi: true
        },
        {
            provide: APP_BASE_HREF,
            useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
            deps: [PlatformLocation]
        },
        DraftFacade,
        FormHelperService,
        UserFacade,
        DatePipe,
        ListingDurationService,
        TimeFromNumberPipe,
        FeeCalculationFacade
    ]
})
export class PropertyUiModule {}
